import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  TouchableOpacity,
  FlatList,
  Dimensions,
  Animated,
  Easing,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { Colors, SIZES } from '../constants/theme';

/**
 * CAROUSEL PUBLICITARIO ROTATIVO — diseño premium nivel Uber Eats / iFood.
 *
 * MODELO DE NEGOCIO
 *   Solo restaurantes con Plan Destacado (RD$ 2,000/mes) aparecen aquí.
 *   El orden se randomiza por sesión → exposición equitativa a todos los
 *   anunciantes pagados.
 *
 * UX
 *   - Auto-advance cada 5s con loop infinito y fade.
 *   - Tap pausa 10s y reanuda solo (no se queda congelado).
 *   - Swipe manual: continúa la rotación desde donde quedó.
 *   - Dot activo se expande con animación suave.
 *
 * VISUAL
 *   - 190px alto, full width con margen lateral.
 *   - Cover del restaurante con kenburns/zoom muy sutil.
 *   - Gradient real (LinearGradient) top-transparent → bottom-dark para
 *     legibilidad.
 *   - Logo circular del restaurante con borde blanco.
 *   - Pill "PATROCINADO" pequeño para compliance (Ley 358-05 RD).
 *   - CTA pill "Ver menú →" abajo-derecha con glass effect.
 *   - Dots de paginación: gris claro → azul Go Safely con animación
 *     de ancho (7px → 22px) al activarse.
 */

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const BANNER_WIDTH = SCREEN_WIDTH - SIZES.padding * 2;
const BANNER_HEIGHT = 190;
const SPACING = 12;
const AUTO_ADVANCE_MS = 5000;
const RESUME_AFTER_TOUCH_MS = 10000;

export interface PromoBannerItem {
  id: string;
  restaurantId: string;
  restaurantName: string;
  restaurantLogoUrl?: string | null;
  restaurantCoverUrl?: string | null;
  title: string;
  description: string;
  color?: string;          // color principal del anunciante (HEX)
  icon?: string;           // emoji opcional
  /** Peso de rotación (1 default). Si un futuro tier paga más, sube su peso. */
  weight?: number;
}

interface Props {
  items: PromoBannerItem[];
  intervalMs?: number;
}

export default function RotatingPromoBanner({ items, intervalMs = AUTO_ADVANCE_MS }: Props) {
  const router = useRouter();
  const listRef = useRef<FlatList<PromoBannerItem>>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  // Orden ponderado + randomizado: cada banner aparece `weight` veces en
  // la lista y luego se barajan. Plan Destacado default weight=1.
  // Si más adelante creas un tier "Diamante" con weight=2, ese restaurante
  // saldrá el doble de seguido sin saltarse el orden equitativo del resto.
  const shuffled = useMemo(() => {
    const weighted: PromoBannerItem[] = [];
    for (const it of items) {
      const w = Math.max(1, it.weight ?? 1);
      for (let k = 0; k < w; k++) weighted.push(it);
    }
    // Fisher-Yates shuffle
    for (let i = weighted.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [weighted[i], weighted[j]] = [weighted[j], weighted[i]];
    }
    return weighted;
  }, [items]);

  useEffect(() => {
    if (paused || shuffled.length <= 1 || !intervalMs) return;
    const id = setInterval(() => {
      setActiveIndex((prev) => {
        const next = (prev + 1) % shuffled.length;
        listRef.current?.scrollToIndex({ index: next, animated: true });
        return next;
      });
    }, intervalMs);
    return () => clearInterval(id);
  }, [paused, shuffled.length, intervalMs]);

  const handleScrollEnd = (e: any) => {
    const offsetX = e.nativeEvent.contentOffset.x;
    const idx = Math.round(offsetX / (BANNER_WIDTH + SPACING));
    if (idx !== activeIndex) setActiveIndex(idx);
  };

  // Reloj de "reanudar tras el toque" (H31, auditoría 16-sep-2026). Antes cada
  // toque lanzaba un setTimeout suelto: al salir de la pantalla antes de los
  // 10 s disparaba sobre un componente ya desmontado, y dos toques seguidos
  // dejaban dos relojes en vuelo — el primero reanudaba la rotación aunque el
  // usuario acabara de volver a tocar. Ahora hay uno solo, se reinicia en cada
  // toque y se limpia al desmontar.
  const reanudarRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (reanudarRef.current) clearTimeout(reanudarRef.current);
  }, []);

  const handleTouchStart = () => {
    setPaused(true);
    if (reanudarRef.current) clearTimeout(reanudarRef.current);
    reanudarRef.current = setTimeout(() => {
      reanudarRef.current = null;
      setPaused(false);
    }, RESUME_AFTER_TOUCH_MS);
  };

  if (shuffled.length === 0) return null;

  return (
    <View style={styles.wrap}>
      <FlatList
        ref={listRef}
        data={shuffled}
        keyExtractor={(item, idx) => `${item.id}-${idx}`}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        snapToInterval={BANNER_WIDTH + SPACING}
        decelerationRate="fast"
        contentContainerStyle={{ paddingHorizontal: SIZES.padding }}
        onMomentumScrollEnd={handleScrollEnd}
        onTouchStart={handleTouchStart}
        getItemLayout={(_d, index) => ({
          length: BANNER_WIDTH + SPACING,
          offset: (BANNER_WIDTH + SPACING) * index,
          index,
        })}
        renderItem={({ item, index }) => (
          <Banner
            item={item}
            isActive={index === activeIndex}
            onPress={() => router.push(`/restaurant/${item.restaurantId}` as any)}
          />
        )}
      />

      {shuffled.length > 1 && (
        <View style={styles.dotsRow}>
          {shuffled.map((_, idx) => (
            <PaginationDot key={idx} active={activeIndex === idx} />
          ))}
        </View>
      )}
    </View>
  );
}

function PaginationDot({ active }: { active: boolean }) {
  const width = useRef(new Animated.Value(active ? 22 : 7)).current;
  useEffect(() => {
    Animated.timing(width, {
      toValue: active ? 22 : 7,
      duration: 280,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [active, width]);
  return (
    <Animated.View
      style={[
        styles.dot,
        active && styles.dotActive,
        { width },
      ]}
    />
  );
}

function Banner({
  item,
  isActive,
  onPress,
}: {
  item: PromoBannerItem;
  isActive: boolean;
  onPress: () => void;
}) {
  const fade = useRef(new Animated.Value(0)).current;
  const imgScale = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    Animated.timing(fade, {
      toValue: 1,
      duration: 400,
      easing: Easing.out(Easing.ease),
      useNativeDriver: true,
    }).start();
  }, [fade]);

  // Kenburns suave en la imagen cuando el banner está activo
  useEffect(() => {
    if (!isActive) return;
    imgScale.setValue(1);
    Animated.timing(imgScale, {
      toValue: 1.08,
      duration: 7000,
      easing: Easing.inOut(Easing.ease),
      useNativeDriver: true,
    }).start();
  }, [isActive, imgScale]);

  const accent = item.color ?? Colors.primary;

  return (
    <TouchableOpacity
      activeOpacity={0.92}
      onPress={onPress}
      style={{ width: BANNER_WIDTH, marginRight: SPACING }}
    >
      <Animated.View
        style={[
          styles.banner,
          {
            opacity: fade,
            transform: [
              {
                translateY: fade.interpolate({
                  inputRange: [0, 1],
                  outputRange: [8, 0],
                }),
              },
            ],
          },
        ]}
      >
        {/* Imagen de cover con kenburns */}
        {item.restaurantCoverUrl ? (
          <Animated.Image
            source={{ uri: item.restaurantCoverUrl }}
            style={[
              StyleSheet.absoluteFill,
              { transform: [{ scale: imgScale }] } as any,
            ]}
            resizeMode="cover"
          />
        ) : (
          <View style={[StyleSheet.absoluteFill, { backgroundColor: accent }]} />
        )}

        {/* Gradient real top → bottom para legibilidad */}
        <LinearGradient
          colors={['rgba(0,0,0,0.05)', 'rgba(0,0,0,0.45)', 'rgba(0,0,0,0.85)']}
          locations={[0, 0.55, 1]}
          style={StyleSheet.absoluteFill}
        />

        {/* Tag PATROCINADO */}
        <View style={styles.sponsoredTag}>
          <Ionicons name="megaphone" size={10} color="#FFF" />
          <Text style={styles.sponsoredTagText}>PATROCINADO</Text>
        </View>

        {/* Logo circular del restaurante */}
        {item.restaurantLogoUrl && (
          <View style={styles.logoWrapper}>
            <Image source={{ uri: item.restaurantLogoUrl }} style={styles.logoCircle} />
          </View>
        )}

        {/* Contenido inferior */}
        <View style={styles.content}>
          {item.icon ? <Text style={styles.iconBig}>{item.icon}</Text> : null}
          <Text style={styles.title} numberOfLines={1}>
            {item.title}
          </Text>
          <Text style={styles.subtitle} numberOfLines={1}>
            {item.description}
          </Text>

          <View style={styles.bottomRow}>
            <View style={styles.restaurantPill}>
              <Ionicons name="storefront" size={11} color="#FFF" />
              <Text style={styles.restaurantPillText} numberOfLines={1}>
                {item.restaurantName}
              </Text>
            </View>

            <View style={styles.ctaPill}>
              <Text style={styles.ctaPillText}>Ver menú</Text>
              <Ionicons name="arrow-forward" size={13} color="#1A1A1A" style={{ marginLeft: 4 }} />
            </View>
          </View>
        </View>
      </Animated.View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  wrap: { marginBottom: 20 },
  banner: {
    width: '100%',
    height: BANNER_HEIGHT,
    borderRadius: 20,
    overflow: 'hidden',
    backgroundColor: '#1A1A1A',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18,
    shadowRadius: 14,
    elevation: 8,
  },
  sponsoredTag: {
    position: 'absolute',
    top: 14,
    left: 14,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.55)',
    paddingHorizontal: 9,
    paddingVertical: 5,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  sponsoredTagText: {
    fontSize: 9,
    color: '#FFF',
    fontWeight: '800',
    letterSpacing: 0.6,
    marginLeft: 5,
  },
  logoWrapper: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#FFF',
    padding: 3,
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 4,
    elevation: 3,
  },
  logoCircle: {
    width: '100%',
    height: '100%',
    borderRadius: 22,
  },
  content: {
    position: 'absolute',
    left: 18,
    right: 18,
    bottom: 16,
  },
  iconBig: {
    fontSize: 24,
    marginBottom: 4,
  },
  title: {
    fontSize: 22,
    fontWeight: '900',
    color: '#FFF',
    letterSpacing: -0.3,
    textShadowColor: 'rgba(0,0,0,0.45)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  subtitle: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.92)',
    marginTop: 3,
    textShadowColor: 'rgba(0,0,0,0.45)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  bottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
  },
  restaurantPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderColor: 'rgba(255,255,255,0.35)',
    borderWidth: 1,
    paddingHorizontal: 11,
    paddingVertical: 6,
    borderRadius: 14,
    maxWidth: '70%',
  },
  restaurantPillText: {
    fontSize: 12,
    color: '#FFF',
    fontWeight: '700',
    marginLeft: 5,
  },
  ctaPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.95)',
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 14,
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 4,
    elevation: 3,
  },
  ctaPillText: {
    fontSize: 12,
    color: '#1A1A1A',
    fontWeight: '800',
  },

  dotsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 12,
  },
  dot: {
    height: 7,
    borderRadius: 4,
    backgroundColor: '#D8DCE3',
    marginHorizontal: 4,
  },
  dotActive: {
    backgroundColor: Colors.primary,
  },
});
